import { AxiosError } from 'axios';

/**
 * Error categories for API errors
 */
export enum ErrorCategory {
  /** Network connectivity issues */
  NETWORK = 'NETWORK',
  /** Authentication/authorization failures */
  AUTH = 'AUTH',
  /** Validation errors (400, 422) */
  VALIDATION = 'VALIDATION',
  /** Server errors (500, 502, 503) */
  SERVER = 'SERVER',
  /** Unknown or unhandled errors */
  UNKNOWN = 'UNKNOWN',
}

/**
 * Custom API error class with categorization
 *
 * Extends Error to provide structured error information
 * for better error handling and user messaging.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public category: ErrorCategory,
    public statusCode?: number,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Categorizes an error based on its type and status code
 *
 * @param error - The error to categorize (unknown type)
 * @returns An ApiError with appropriate category
 */
export function categorizeError(error: unknown): ApiError {
  // Handle AxiosError
  if (error instanceof AxiosError) {
    const statusCode = error.response?.status;

    // Network errors (no response)
    if (!error.response) {
      return new ApiError(
        'Network error. Please check your connection.',
        ErrorCategory.NETWORK,
        undefined,
        error
      );
    }

    // Authentication errors (401, 403)
    if (statusCode === 401 || statusCode === 403) {
      return new ApiError(
        statusCode === 401
          ? 'Please log in to continue.'
          : 'You do not have permission to access this resource.',
        ErrorCategory.AUTH,
        statusCode,
        error
      );
    }

    // Validation errors (400, 422)
    if (statusCode === 400 || statusCode === 422) {
      const message = error.response?.data?.message || 'Invalid request. Please check your input.';
      return new ApiError(message, ErrorCategory.VALIDATION, statusCode, error);
    }

    // Server errors (500, 502, 503, 504)
    if (statusCode && statusCode >= 500) {
      return new ApiError(
        'Server error. Please try again later.',
        ErrorCategory.SERVER,
        statusCode,
        error
      );
    }

    // Other HTTP errors
    return new ApiError(
      error.response?.data?.message || error.message || 'An error occurred.',
      ErrorCategory.UNKNOWN,
      statusCode,
      error
    );
  }

  // Handle ApiError instances (already categorized)
  if (error instanceof ApiError) {
    return error;
  }

  // Handle generic Error instances
  if (error instanceof Error) {
    return new ApiError(error.message, ErrorCategory.UNKNOWN, undefined, error);
  }

  // Handle unknown error types
  return new ApiError('An unexpected error occurred.', ErrorCategory.UNKNOWN, undefined, error);
}

/**
 * Get user-friendly error message based on error category
 */
export function getUserFriendlyMessage(error: ApiError): string {
  // If error already has a specific message, use it
  if (error.message && error.message !== 'An unexpected error occurred.') {
    return error.message;
  }

  // Provide category-specific fallback messages
  switch (error.category) {
    case ErrorCategory.NETWORK:
      return 'Unable to connect. Please check your internet connection and try again.';

    case ErrorCategory.AUTH:
      return error.statusCode === 401
        ? 'Your session has expired. Please log in again.'
        : 'You do not have permission to perform this action.';

    case ErrorCategory.VALIDATION:
      return 'Please check your input and try again.';

    case ErrorCategory.SERVER:
      return 'Our servers are experiencing issues. Please try again in a few moments.';

    case ErrorCategory.UNKNOWN:
    default:
      return 'Something went wrong. Please try again.';
  }
}

/**
 * Check if an error should be retried automatically
 */
export function isRetryableError(error: ApiError): boolean {
  // Network errors and server errors are usually retryable
  if (error.category === ErrorCategory.NETWORK || error.category === ErrorCategory.SERVER) {
    return true;
  }

  // Specific status codes that are retryable
  if (error.statusCode === 408 || error.statusCode === 429 || error.statusCode === 503) {
    return true;
  }

  return false;
}
